// ── V137: 발주 내역 합치기 ─────────────────────────────────────────────────
// 같은 날 따로 저장한 발주서(양파 1 → 저장, 감자 1 → 저장)를 골라 하나로 합친다.
// 원래 발주서들은 지우고 합친 발주서 하나만 남긴다 (호출하는 쪽에서 한 번에 바꿔 써야 서로 덮어쓰지 않음).
//   - 같은 품목(이름·규격·단위·발주처·단가가 모두 같음)은 수량을 더한다. 단가가 다르면 따로 둔다
//   - 날짜는 고른 것 중 가장 늦은 날. 다른 달끼리는 합치지 않는다 (월 정산 금액이 옮겨가므로) → canMergeOrders
//   - 합계 = 원래 합계들의 합, 메모는 겹치지 않게 ' / '로 이어 붙임

const round = (n, d) => { const f = 10 ** d; return Math.round((Number(n) || 0) * f) / f; };
const KEY_SEP = '\u0001';
const itemKey = (i) => [i.name, i.spec || '-', i.unit || '개', i.vendor || '미분류', Number(i.price) || 0].join(KEY_SEP);

// 오래된 것부터 (날짜 → 저장 순서)
const byOld = (a, b) => (String(a.date || '') < String(b.date || '') ? -1 : String(a.date || '') > String(b.date || '') ? 1
  : (Number(a.id) || 0) - (Number(b.id) || 0));

/** 합칠 수 있나: 2개 이상, 같은 달 */
export function canMergeOrders(entries) {
  const list = (entries || []).filter(Boolean);
  if (list.length < 2) return { ok: false, reason: 'few' };
  const months = new Set(list.map(e => String(e.date || '').slice(0, 7)));
  if (months.size > 1) return { ok: false, reason: 'month' };
  return { ok: true };
}

/** 고른 발주서들을 하나로 */
export function mergeOrderEntries(entries, { id = String(Date.now()) } = {}) {
  const list = (entries || []).filter(Boolean).slice().sort(byOld);
  const latest = list[list.length - 1] || {};
  const map = new Map();
  for (const e of list) {
    for (const it of (e.items || [])) {
      const k = itemKey(it);
      const cur = map.get(k);
      if (!cur) { map.set(k, { ...it, qty: Number(it.qty) || 0 }); continue; }
      cur.qty = round(cur.qty + (Number(it.qty) || 0), 3);
      // V135 묶음 정보: 묶음 크기가 같으면 묶음 수도 더하고, 다르면 뺀다
      if (cur.pack && it.pack && cur.pack === it.pack) cur.packs = round((cur.packs || 0) + (it.packs || 0), 3);
      else { delete cur.pack; delete cur.packs; }
    }
  }
  const memos = [];
  for (const e of list) { const m = String(e.memo || '').trim(); if (m && !memos.includes(m)) memos.push(m); }
  return {
    id,
    date: latest.date,
    bizName: latest.bizName || '',
    bizInfo: latest.bizInfo || {},
    memo: memos.join(' / '),
    items: [...map.values()],
    totalCost: round(list.reduce((s, e) => s + (Number(e.totalCost) || 0), 0), 2),
    mergedFrom: list.map(e => e.id),
    // V138: 발주처가 하나뿐이면 그 발주처 발주서로
    ...(() => { const vs = new Set(list.map(e => entryVendor(e))); return vs.size === 1 && [...vs][0] ? { vendor: [...vs][0] } : {}; })(),
  };
}

/** 발주서의 발주처 — V138부터는 entry.vendor, 예전 것은 품목이 모두 같은 발주처일 때만 */
export function entryVendor(entry) {
  if (!entry) return '';
  if (entry.vendor) return entry.vendor;
  const vs = new Set((entry.items || []).map(i => String(i.vendor || '').trim() || '미분류'));
  return vs.size === 1 ? [...vs][0] : '';
}

/** V138: 수량 고치기 — edits: { 품목 순번: 새 값 } (묶음 품목은 묶음 수, 아니면 수량). 0이면 그 품목을 뺀다 */
export function applyQtyEdits(entry, edits, { now = Date.now() } = {}) {
  const items = [];
  (entry.items || []).forEach((it, i) => {
    const has = edits && Object.prototype.hasOwnProperty.call(edits, i);
    const v = has ? Number(edits[i]) : null;
    if (!has || !Number.isFinite(v)) { items.push(it); return; }
    if (v <= 0) return;
    if (it.pack > 1) items.push({ ...it, packs: round(v, 3), qty: round(v * it.pack, 3) });
    else items.push({ ...it, qty: round(v, 3) });
  });
  return {
    ...entry,
    items,
    totalCost: round(items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0), 2),
    editedAt: now,
  };
}

/** 발주처별로 나눈 저장용 발주서들 — groups: [{ vendor, items(저장용), totalCost }] */
export function splitByVendor(base, groups, { now = Date.now() } = {}) {
  return groups.map((g, i) => ({
    ...base,
    id: String(now + i),
    vendor: g.vendor,
    items: g.items,
    totalCost: g.totalCost,
  }));
}

/** 저장 시각 (id = 저장한 순간의 밀리초) → "14:32", 모르면 '' */
export function savedTimeOf(entry) {
  const n = Number(entry && entry.id);
  if (!(n > 1e12)) return '';
  const d = new Date(n);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
